<template>
  <div class="relationship-cell-wrapper">
    <span
      :id="cellId"
      ref="cellTrigger"
      class="relationship-cell"
      @click="openEditor"
    >
{{ displayText }}
    </span>
    <b-popover
      v-if="popoverVisible"
      :target="cellId"
      :manual="true"
      v-model="popoverVisible"
      placement="top"
      teleport-to="body"
      custom-class="relationship-editor-popover"
      :no-auto-close="true"
      @hidden="onPopoverHidden"
    >
      <template #title>
        {{ fieldLabel }}
      </template>
      <div class="relationship-editor-content">
        <!-- Current linked records -->
        <div v-if="linkedRecords.length > 0" class="linked-records mb-2">
          <div
            v-for="rec in linkedRecords"
            :key="rec.id"
            class="linked-record-tag"
          >
            <span class="linked-record-label">{{ rec.displayValue }}</span>
            <span class="linked-record-remove" @click="unlinkRecord(rec.id)">&times;</span>
          </div>
        </div>
        <div v-else class="text-muted mb-2" style="font-size: 0.8rem;">No linked records</div>

        <!-- Search input -->
        <b-form-input
          ref="searchInput"
          v-model="searchQuery"
          size="sm"
          placeholder="Search to link..."
          class="mb-2"
          @input="onSearchInput"
          @keyup.enter="linkFirst"
        />

        <!-- Search results -->
        <div v-if="searchResults.length > 0" class="search-results">
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="search-result-item"
            @click="linkRecord(result.id)"
          >
            <span class="search-result-label">{{ result.displayValue }}</span>
            <span class="search-result-id">#{{ result.id }}</span>
          </div>
        </div>
        <div v-else-if="searchQuery && !searching" class="text-muted" style="font-size: 0.8rem;">No results</div>
        <div v-if="searching" class="text-muted" style="font-size: 0.8rem;">Searching...</div>
        <div v-if="linkError" class="text-danger" style="font-size: 0.8rem;">{{ linkError }}</div>
        <button v-if="allowAdd" type="button" class="btn btn-link btn-sm px-0" @click="adding = !adding">{{ adding ? 'Cancel' : 'Add' }}</button>
        <div v-if="adding">
          <div v-for="field in addFields" :key="field.uniqueTitle" class="mb-1">
            <label class="d-block" style="font-size: 0.75rem;">{{ field.title }}</label>
            <b-form-input v-model="addValues[field.uniqueTitle]" size="sm" />
          </div>
          <b-button size="sm" variant="primary" @click="submitAdd">Save and link</b-button>
          <div v-if="addedLabel" class="text-success" style="font-size: 0.8rem;">Added {{ addedLabel }}</div>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button size="sm" variant="secondary" @click="closeEditor">Close</b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
export default {
  name: 'RelationshipEditor',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      required: true
    },
    fieldKey: {
      type: String,
      required: true
    },
    fieldLabel: {
      type: String,
      default: ''
    },
    columnMeta: {
      type: Object,
      default: () => ({})
    },
    spreadsheetUniqueTitle: {
      type: String,
      required: true
    },
    applicationUniqueTitle: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      popoverVisible: false,
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchTimeout: null,
      linkError: '',
      allowAdd: false,
      addFields: [],
      addValues: {},
      adding: false,
      addedLabel: ''
    }
  },
  mounted () {
    this.$nextTick(() => this.openEditor())
  },
  beforeUnmount () {
    // Safety net: clean up listener if still registered
    if (this._onCellEditorOpen) {
      document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
    }
  },
  computed: {
    cellId () {
      const id = (this.item && this.item.id) != null ? this.item.id : Math.random().toString(36).slice(2)
      return `rel-cell-${id}-${this.fieldKey}`
    },
    linkedRecords () {
      if (!Array.isArray(this.value)) return []
      return this.value.map(rec => {
        if (typeof rec === 'object' && rec !== null) {
          // Find display value - first non-id, non-metadata key
          const displayKey = Object.keys(rec).find(k => k !== 'id' && k !== 'createdAt' && k !== 'updatedAt' && !k.endsWith('_from') && !k.endsWith('_to'))
            return {
            id: rec.id,
            displayValue: rec.displayLabel || (displayKey ? (rec[displayKey] != null ? String(rec[displayKey]) : `#${rec.id}`) : `#${rec.id}`)
          }
        }
        return { id: rec, displayValue: `#${rec}` }
      })
    },
    displayText () {
      if (this.linkedRecords.length === 0) return '—'
      return this.linkedRecords.map(r => r.displayValue).join(', ')
    },
    isMultiple () {
      return this.columnMeta.relationshipType === 'manyToMany' || this.columnMeta.relationshipType === 'oneToMany'
    },
    relatedSpreadSheet () {
      return this.columnMeta.relatedSpreadSheet || ''
    }
  },
  methods: {
    _registerCloseListener () {
      if (this._onCellEditorOpen) return // already registered
      this._onCellEditorOpen = (e) => {
        if (e.detail !== this.cellId && this.popoverVisible) {
          this.popoverVisible = false
          this._unregisterCloseListener()
        }
      }
      document.addEventListener('cell-editor-open', this._onCellEditorOpen)
    },
    _unregisterCloseListener () {
      if (this._onCellEditorOpen) {
        document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
        this._onCellEditorOpen = null
      }
    },
    openEditor () {
      document.dispatchEvent(new CustomEvent('cell-editor-open', { detail: this.cellId }))
      this.popoverVisible = true
      this._registerCloseListener()
      this.searchQuery = ''
      this.searchResults = []
      this.$nextTick(() => {
        const input = this.$refs.searchInput
        if (input && input.$el) {
          input.$el.focus()
        } else if (input) {
          input.focus()
        }
      })
    },
    closeEditor () {
      this.popoverVisible = false
      this._unregisterCloseListener()
    },
    onPopoverHidden () {
      this._unregisterCloseListener()
      this.searchQuery = ''
      this.searchResults = []
    },
    onSearchInput () {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.doSearch()
      }, 250)
    },
    async doSearch () {
      if (!this.relatedSpreadSheet) return

      this.searching = true
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadsheetUniqueTitle}/relationships/${this.fieldKey}/options`,
          {
            params: {
              q: this.searchQuery,
              forLink: 1,
              recordId: this.item && this.item.id,
              row: JSON.stringify(this.item || {})
            },
            headers: { 'application-unique-title': this.applicationUniqueTitle }
          }
        )
        const payload = response.data || {}
        this.allowAdd = !!payload.allowAdd
        this.addFields = payload.addFields || []
        const linkedIds = new Set(this.linkedRecords.map(r => r.id))
        this.searchResults = (payload.options || []).filter(r => !linkedIds.has(r.id))
      } catch (error) {
        console.error('Relationship search failed:', error)
        this.searchResults = []
      }
      this.searching = false
    },
    async linkRecord (targetId) {
      this.linkError = ''
      try {
        await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadsheetUniqueTitle}/${this.item.id}/link/${this.fieldKey}`,
          { targetId, row: this.item },
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.$emit('relationship-changed')

        // For non-many-to-many, close after linking
        if (!this.isMultiple) {
          this.closeEditor()
        } else {
          // Refresh search to remove linked item from results
          this.doSearch()
        }
      } catch (error) {
        const data = error && error.response && error.response.data
        this.linkError = typeof data === 'string' && data.trim() ? data : 'Could not link that record'
      }
    },
    async submitAdd () {
      this.linkError = ''
      try {
        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadsheetUniqueTitle}/${this.item.id}/link/${this.fieldKey}/add`,
          { values: this.addValues, row: this.item },
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.addedLabel = (response.data && (response.data.label || response.data.displayValue)) || ''
        this.adding = false
        this.addValues = {}
        this.$emit('relationship-changed')
      } catch (error) {
        const data = error && error.response && error.response.data
        this.linkError = typeof data === 'string' && data.trim() ? data : 'Could not add that record'
      }
    },
    async unlinkRecord (targetId) {
      try {
        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.spreadsheetUniqueTitle}/${this.item.id}/link/${this.fieldKey}/${targetId}`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.$emit('relationship-changed')

        // Refresh search results if popover is open
        if (this.popoverVisible && this.searchQuery) {
          this.doSearch()
        }
      } catch (error) {
        console.error('Failed to unlink record:', error)
      }
    },
    linkFirst () {
      if (this.searchResults.length > 0) {
        this.linkRecord(this.searchResults[0].id)
      }
    }
  }
}
</script>

<!-- Scoped styles for the inline cell trigger only -->
<style scoped>
.relationship-cell-wrapper {
  width: 100%;
}
.relationship-cell {
  cursor: pointer;
  padding: 4px 8px;
  display: block;
  width: 100%;
  border-radius: 3px;
  min-height: 1.4em;
  transition: background-color 0.1s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}
.relationship-cell:hover {
  background-color: rgba(154, 209, 43, 0.1);
  outline: 1px solid rgba(154, 209, 43, 0.3);
}
</style>

<!-- Global styles for popover content (teleported to body, outside scoped style reach) -->
<style>
.relationship-editor-popover {
  max-width: 320px !important;
  z-index: 1060;
}

.relationship-editor-popover .popover-body {
  background-color: #2d2d2a;
  color: #e8e8e6;
  padding: 12px;
}

.relationship-editor-popover .popover-header {
  background-color: #1e1e1b;
  color: #e8e8e6;
  border-color: #474842;
  font-size: 0.85rem;
}

.relationship-editor-popover .form-control {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #474842;
}

.relationship-editor-popover .form-control:focus {
  background-color: #181915;
  color: #e8e8e6;
  border-color: #9ad12b;
  box-shadow: 0 0 0 0.2rem rgba(154, 209, 43, 0.25);
}

.relationship-editor-content {
  width: 100%;
}

.linked-records {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.linked-record-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #333330;
  border: 1px solid #474842;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.8rem;
  color: #e8e8e6;
}
.linked-record-label {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.linked-record-remove {
  cursor: pointer;
  color: #888;
  font-size: 1rem;
  line-height: 1;
  padding: 0 2px;
}
.linked-record-remove:hover {
  color: #ff6b6b;
}

.search-results {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #474842;
  border-radius: 4px;
  background-color: #1e1e1b;
}
.search-result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 0.82rem;
  color: #e8e8e6;
  transition: background-color 0.1s;
}
.search-result-item:hover {
  background-color: rgba(154, 209, 43, 0.15);
}
.search-result-item + .search-result-item {
  border-top: 1px solid #3a3a37;
}
.search-result-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.search-result-id {
  color: #666;
  font-size: 0.75rem;
  margin-left: 8px;
  flex-shrink: 0;
}
</style>
